import { defineAsyncComponent } from '../../lib/utils/vue3compat/vue.js?vue3';
import { GlAvatar, GlIcon, GlDisclosureDropdown, GlDisclosureDropdownGroup, GlDisclosureDropdownItem, GlButton, GlToggle, GlModalDirective, GlTooltipDirective } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import UserMenuUpgradeSubscription from '../../../../../ee/app/assets/javascripts/super_sidebar/components/user_menu_upgrade_subscription.vue?vue3';
import UserMenuExplorePremium from '../../../../../ee/app/assets/javascripts/super_sidebar/components/user_menu_explore_premium.vue?vue3';
import { s__, __, sprintf } from '~/locale';
import Tracking from '../../tracking/index.js?vue3';
import { SET_STATUS_MODAL_ID } from '~/set_status_modal/constants';
import axios from '../../lib/utils/axios_utils.js?vue3';
import { visitUrl, refreshCurrentPage } from '~/lib/utils/url_utility';
import { setGitlabNext } from '~/lib/utils/gitlab_next';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { logError } from '~/lib/logger';
import WhatsNewForYouMenuItem from '../../whats_new/components/whats_new_for_you_menu_item.vue?vue3';
import { adminImpersonationPath, adminRootPath, destroyAdminSessionPath, newAdminSessionPath } from '~/lib/utils/path_helpers/instance_admin';
import { profilePreferencesPath } from '~/lib/utils/path_helpers/profile';
import { destroyUserSessionPath } from '~/lib/utils/path_helpers/routes';
import { userPath } from '~/lib/utils/path_helpers/user';
import { userSettingsProfilePath } from '~/lib/utils/path_helpers/user_settings';
import { USER_MENU_TRACKING_DEFAULTS } from '../constants';
import UserMenuProfileItem from './user_menu_profile_item.vue?vue3';
import UserCounts from './user_counts.vue?vue3';
export default {
    name: 'UserMenu',
    SET_STATUS_MODAL_ID,
    WHATS_NEW_TRACKING_PROPERTY: USER_MENU_TRACKING_DEFAULTS['data-track-property'],
    // "GitLab Next" is a proper noun, so it is intentionally not translated
    GITLAB_NEXT_LABEL: 'GitLab Next',
    i18n: {
        setStatus: s__('SetStatusModal|Set status'),
        editStatus: s__('SetStatusModal|Edit status'),
        editProfile: s__('CurrentUser|Edit profile'),
        preferences: s__('CurrentUser|Preferences'),
        buyPipelineMinutes: s__('CurrentUser|Buy compute minutes'),
        oneOfGroupsRunningOutOfPipelineMinutes: s__('CurrentUser|One of your groups is running out'),
        adminArea: s__('Navigation|Admin'),
        enterAdminMode: s__('CurrentUser|Enter Admin Mode'),
        leaveAdminMode: s__('CurrentUser|Leave Admin Mode'),
        stopImpersonating: __('Stop impersonating'),
        signOut: __('Sign out')
    },
    components: {
        GlAvatar,
        GlIcon,
        GlDisclosureDropdown,
        GlDisclosureDropdownGroup,
        GlDisclosureDropdownItem,
        GlButton,
        GlToggle,
        UserCounts,
        UserMenuProfileItem,
        UserMenuUpgradeSubscription,
        UserMenuExplorePremium,
        WhatsNewForYouMenuItem,
        SetStatusModal: defineAsyncComponent(()=>import(/* webpackChunkName: 'statusModalBundle' */ '../../set_status_modal/set_status_modal_wrapper.vue?vue3'))
    },
    directives: {
        GlModal: GlModalDirective,
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        Tracking.mixin()
    ],
    inject: [
        'isImpersonating',
        'isSaas'
    ],
    props: {
        data: {
            required: true,
            type: Object
        }
    },
    data () {
        return {
            setStatusModalReady: false,
            updatedAvatarUrl: null
        };
    },
    computed: {
        avatarUrl () {
            return this.updatedAvatarUrl || this.data.avatar_url;
        },
        toggleText () {
            return sprintf(__('%{user} user’s menu'), {
                user: this.data.name
            });
        },
        isAdmin () {
            return this.data?.admin_mode?.user_is_admin;
        },
        adminLinkItem () {
            return {
                text: this.$options.i18n.adminArea,
                href: adminRootPath({
                    organizationPath: null
                })
            };
        },
        statusLabel () {
            const { busy, customized } = this.data.status;
            return busy || customized ? this.$options.i18n.editStatus : this.$options.i18n.setStatus;
        },
        statusItem () {
            return {
                text: this.statusLabel,
                extraAttrs: {
                    ...USER_MENU_TRACKING_DEFAULTS,
                    'data-track-label': 'user_edit_status'
                }
            };
        },
        editProfileItem () {
            return {
                text: this.$options.i18n.editProfile,
                href: userSettingsProfilePath({
                    organizationPath: null
                }),
                extraAttrs: {
                    'data-testid': 'edit-profile-link',
                    ...USER_MENU_TRACKING_DEFAULTS,
                    'data-track-label': 'user_edit_profile'
                }
            };
        },
        preferencesItem () {
            return {
                text: this.$options.i18n.preferences,
                href: profilePreferencesPath({
                    organizationPath: null
                }),
                extraAttrs: {
                    ...USER_MENU_TRACKING_DEFAULTS,
                    'data-track-label': 'user_preferences'
                }
            };
        },
        addBuyPipelineMinutesMenuItem () {
            return this.data.pipeline_minutes?.show_buy_pipeline_minutes;
        },
        buyPipelineMinutesItem () {
            return {
                text: this.$options.i18n.buyPipelineMinutes,
                warningText: this.$options.i18n.oneOfGroupsRunningOutOfPipelineMinutes,
                href: this.data.pipeline_minutes?.buy_pipeline_minutes_path,
                extraAttrs: {
                    ...USER_MENU_TRACKING_DEFAULTS,
                    'data-track-label': 'buy_pipeline_minutes'
                }
            };
        },
        isOnGitlabNext () {
            return Boolean(this.data.gitlab_com_and_canary);
        },
        showGitlabNextItem () {
            return this.isSaas;
        },
        gitlabNextTrackingAttrs () {
            return {
                ...USER_MENU_TRACKING_DEFAULTS,
                'data-track-action': 'toggle',
                'data-track-label': this.isOnGitlabNext ? 'leave_canary' : 'switch_to_canary'
            };
        },
        enterAdminModeItem () {
            return {
                text: this.$options.i18n.enterAdminMode,
                href: newAdminSessionPath(),
                extraAttrs: {
                    ...USER_MENU_TRACKING_DEFAULTS,
                    'data-track-label': 'enter_admin_mode'
                }
            };
        },
        leaveAdminModeItem () {
            return {
                text: this.$options.i18n.leaveAdminMode,
                href: destroyAdminSessionPath(),
                extraAttrs: {
                    ...USER_MENU_TRACKING_DEFAULTS,
                    'data-track-label': 'leave_admin_mode',
                    'data-method': 'post'
                }
            };
        },
        signOutItem () {
            return {
                text: this.$options.i18n.signOut,
                href: destroyUserSessionPath({
                    organizationPath: null
                }),
                extraAttrs: {
                    'data-method': 'post',
                    'data-testid': 'sign-out-link',
                    class: 'sign-out-link'
                }
            };
        },
        statusModalData () {
            if (!this.data?.status?.can_update) {
                return null;
            }
            const { busy, customized } = this.data.status;
            if (!busy && !customized) {
                return {};
            }
            const { emoji, message, availability, clear_after: clearAfter } = this.data.status;
            return {
                'current-emoji': emoji || '',
                'current-message': message || '',
                'current-availability': availability || '',
                'current-clear-status-after': clearAfter || ''
            };
        },
        showAdminButton () {
            return this.isAdmin && (!this.data.admin_mode.admin_mode_feature_enabled || this.data.admin_mode.admin_mode_active);
        },
        showEnterAdminModeItem () {
            return this.data.admin_mode.user_is_admin && this.data.admin_mode.admin_mode_feature_enabled && !this.data.admin_mode.admin_mode_active;
        },
        showLeaveAdminModeItem () {
            return this.data.admin_mode.user_is_admin && this.data.admin_mode.admin_mode_feature_enabled && this.data.admin_mode.admin_mode_active;
        },
        showNotificationDot () {
            return this.data.pipeline_minutes?.show_notification_dot;
        },
        hasEmoji () {
            return this.data?.status?.emoji;
        }
    },
    mounted () {
        document.addEventListener('userAvatar:update', this.updateAvatar);
    },
    unmounted () {
        document.removeEventListener('userAvatar:update', this.updateAvatar);
    },
    methods: {
        adminImpersonationPath,
        userPath (username) {
            return userPath(username, {
                organizationPath: null
            });
        },
        updateAvatar (event) {
            this.updatedAvatarUrl = event.detail?.url;
        },
        onShow () {
            this.initBuyCIMinsCallout();
            this.$refs.explorePremiumItem?.handleMenuShown?.();
        },
        openStatusModal () {
            this.setStatusModalReady = true;
            this.$refs.userDropdown.close();
        },
        initBuyCIMinsCallout () {
            const el = this.$refs?.buyPipelineMinutesNotificationCallout?.$el;
            el?.addEventListener('click', this.onBuyCIMinutesItemClick);
        },
        async onBuyCIMinutesItemClick (event) {
            /* NOTE: We're not sure this event is tracked by anyone
       * whether it stays will depend on the outcome of this discussion:
       * https://gitlab.com/gitlab-org/gitlab/-/issues/402713#note_1343072135
       */ const { 'track-action': trackAction, 'track-label': label, 'track-property': property } = this.data.pipeline_minutes.tracking_attrs;
            this.track(trackAction, {
                label,
                property
            });
            // Proceed to the URL if the notification dot is not shown
            if (!this.showNotificationDot) return;
            event.preventDefault();
            const href = this.data.pipeline_minutes?.buy_pipeline_minutes_path;
            const featureId = this.data.pipeline_minutes.callout_attrs.feature_id;
            const dismissEndpoint = this.data.pipeline_minutes.callout_attrs.dismiss_endpoint;
            try {
                // dismiss the notification dot Callout
                await axios.post(dismissEndpoint, {
                    feature_name: featureId
                });
            } catch (error) {
                logError(error);
                // Override gon.feature_category as this code loads on all pages
                Sentry.captureException(error, {
                    tags: {
                        feature_category: 'navigation'
                    }
                });
            } finally{
                // visit the URL whether the callout notification is dismissed or not
                visitUrl(href);
            }
        },
        toggleGitlabNext (enabled) {
            setGitlabNext(enabled);
            refreshCurrentPage();
        },
        trackSignOut () {
            this.track(USER_MENU_TRACKING_DEFAULTS['data-track-action'], {
                label: 'user_sign_out',
                property: USER_MENU_TRACKING_DEFAULTS['data-track-property']
            });
        }
    }
};
